import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IFilterItem } from '../../../data-types/filterItem';
import { useFilterPickerState } from '../../../hooks/useFilterPickerState';
import { getFilterMenuAriaLabel, getUpdatedSelectedFilters, reorderCheckedItems } from '../../../utils/FilterUtils';
import FilterPickerPopover from '../filter-picker/FilterPickerPopover';

export interface IFilterButtonProps {
    label?: string;
    items: IFilterItem[];
    filterName: string;
    checkedItems: IFilterItem[];
    searchValue: string;
    noElementsFoundtext: string;
    setSearchValue: (value: string) => void;
    setCheckedItems: (items: IFilterItem[]) => void;
    onButtonClick?: (filterName: string) => void;
    disabled?: boolean;
}

/** @deprecated Prefer local styles. */
export const getButtonStyles = () => ({
    filterItemWrapperStyles: {
        paddingTop: 5,
        paddingBottom: 5,
    },
});

const FilterButton: React.FC<IFilterButtonProps> = ({
    label,
    filterName,
    items,
    checkedItems,
    searchValue,
    noElementsFoundtext,
    disabled,
    setSearchValue,
    setCheckedItems,
    onButtonClick,
}) => {
    const { t } = useTranslation();
    const isItemSelected = checkedItems.length > 0;

    const handleCommit = (filteredItems: IFilterItem[], committedFilterName: string) => {
        const newSelectedItems: IFilterItem[] = getUpdatedSelectedFilters(filteredItems, checkedItems);
        setCheckedItems(newSelectedItems);
        if (onButtonClick) {
            onButtonClick(committedFilterName);
        }
    };

    const orderedItems = useMemo(() => {
        return reorderCheckedItems(checkedItems, items, filterName);
    }, [checkedItems, items, filterName]);

    const picker = useFilterPickerState({
        items: orderedItems,
        committedSelection: checkedItems,
        filterName,
        isFiltered: isItemSelected,
        noResultsText: noElementsFoundtext,
        searchItemName: 'item',
        searchValue,
        setSearchValue,
        onCommit: handleCommit,
    });

    const buttonId = `${filterName}-button`;
    return (
        <FilterPickerPopover
            buttonId={buttonId}
            label={t(label || filterName)}
            ariaLabel={t(label || filterName) ?? ''}
            isActive={isItemSelected}
            isDisabled={disabled}
            filterMenuAriaLabel={getFilterMenuAriaLabel(label || filterName, t)}
            searchBoxPlaceholderText={t('search')}
            clearAllText={t('clearAll')}
            isMultiSelect
            draftSelectedIds={picker.draftSelectedIds}
            visibleItems={picker.visibleItems}
            hasResults={picker.hasResults}
            noResultsText={noElementsFoundtext}
            announcementMessage={picker.announcementMessage}
            showClearAll={picker.showClearAll}
            onSearchChange={picker.onSearchChange}
            onAbortSearch={picker.onAbortSearch}
            onSelectionChange={picker.onSelectionChange}
            onClearAll={picker.clearAll}
            onApply={() => picker.apply()}
            onCancel={() => picker.cancel()}
            onDismiss={picker.resetDraft}
        />
    );
};

export default FilterButton;
